import { Button, Form, FormTitle, Input, Modal, Select, SubContent, Table } from '@maxtropy/components';
import { Col, Row, Space, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalysisUnit } from './components/BindUnitModal';
import EnergyStatisticsRow from './components/EnergyStatisticsRow';
import EnergyFlowRow from './components/EnergyFlowRow';
import AreaListTable from './components/AreaListTable';
import DeptListTable from './components/DeptListTable';
import EnvConfig from './components/EnvConfig';
import EnergySavingConfig from './components/EnergySavingConfig';
import styles from './index.module.scss';
import {
  apiV2CircuitListPost,
  apiV2EnergyAnalysisListByOuPost,
  apiV2EnergyOverviewConfigDetailPost,
  apiV2InspectionRecorderListPost,
  apiV2RobotMonitorListPost,
  V2EnergyOverviewConfigDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { getDataQueryPeriodList, GetDataQueryPeriodListRes } from '@/api/energyOverview';
import { initFormValues, handleOnFinish } from './util';
import UploadImport from './components/UploadImport';

interface Option {
  label?: number | string;
  value?: number | string;
}
const EnergyOverview = () => {
  const [form] = Form.useForm();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [nonWorkingPeriods, setNonWorkingPeriods] = useState<Option[]>([]); //非工作时段
  const [unitGroup, setUnitGroup] = useState<AnalysisUnit[]>([]); // 用能分析组列表
  const [circuitIdOp, setCircuitIdOp] = useState<Option[]>([]); // 回路选项
  const [inspectionRecorderOptions, setInspectionRecorderOptions] = useState<Option[]>([]); // 巡检记录仪选项
  const [robotOptions, setRobotOptions] = useState<Option[]>([]); //机器人
  const [openUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(true);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [mediumLoadingCount, setMediumLoadingCount] = useState<number>(0);

  useEffect(() => {
    Promise.all([
      // 获取用能分析组
      apiV2EnergyAnalysisListByOuPost({}).then(res => {
        setUnitGroup((res.list ?? []) as AnalysisUnit[]);
      }),
      // 获取非工作时段
      getDataQueryPeriodList({}).then((res: GetDataQueryPeriodListRes) => {
        setNonWorkingPeriods(res.dataPeriodList?.map(i => ({ label: i.name, value: i.id })) ?? []);
      }),
      // 回路
      apiV2CircuitListPost({}).then(res => {
        setCircuitIdOp((res.list ?? []).map(i => ({ label: i.name, value: i.id })));
      }),
      // 机器人/机器狗视频监控列表
      apiV2RobotMonitorListPost({}).then(res => {
        setRobotOptions(
          (res.list ?? [])
            .filter(item => item.id != null && item.deviceName)
            .map(item => ({ label: item.deviceName, value: item.id }))
        );
      }),
      //巡检记录仪列表
      apiV2InspectionRecorderListPost({}).then(res => {
        setInspectionRecorderOptions(res?.list?.map(i => ({ label: i.deviceName, value: i.id })) ?? []);
      }),
    ])
      .catch(() => undefined)
      .finally(() => setOptionsLoading(false));
  }, []);

  // 获取配置详情
  useEffect(() => {
    setDetailLoading(true);
    apiV2EnergyOverviewConfigDetailPost({})
      .then((res: V2EnergyOverviewConfigDetailPostResponse) => {
        form.setFieldsValue(initFormValues(res));
      })
      .catch(() => undefined)
      .finally(() => setDetailLoading(false));
  }, [refresh]);

  // 提交表单
  const onFinish = (values: any) => {
    setSaveLoading(true);
    handleOnFinish(values, {
      setIsEdit,
      setRefresh,
      form,
    }).finally(() => setSaveLoading(false));
  };

  //下拉框统一属性
  const commonSelectProps = useMemo(() => {
    return {
      optionFilterProp: 'label',
      placeholder: '请选择',
      disabled: !isEdit,
      allowClear: true,
      showSearch: true,
    };
  }, [isEdit]);

  const handleMediumLoadingChange = useCallback((loading: boolean) => {
    setMediumLoadingCount(count => Math.max(loading ? count + 1 : count - 1, 0));
  }, []);

  const renderInspectionConfigList = (listName: 'robotConfigs' | 'dogConfigs', title: string) => (
    <Form.List name={listName}>
      {(fields, { add, remove }) => (
        <div className={styles.inspectionConfigList}>
          <div className={styles.inspectionConfigTitle}>{title}</div>
          <Table
            rowKey="key"
            dataSource={fields}
            pagination={false}
            columns={[
              {
                title: '名称',
                width: 300,
                dataIndex: 'name',
                render: (_: unknown, field) => (
                  <Form.Item
                    name={[field.name, 'name']}
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        validator: async (_, value?: string) => {
                          const currentName = value?.trim();
                          if (!currentName) return;
                          const configs = form.getFieldValue(listName) ?? [];
                          const sameNameCount = configs.filter((item: { name?: string }) => {
                            return item?.name?.trim() === currentName;
                          }).length;
                          if (sameNameCount > 1) {
                            throw new Error('名称不能重复');
                          }
                        },
                      },
                    ]}
                  >
                    <Input disabled={!isEdit} placeholder="请输入" />
                  </Form.Item>
                ),
              },
              {
                title: '可见光',
                width: 300,
                dataIndex: 'visibleLightRecorderId',
                render: (_: unknown, field) => (
                  <Form.Item name={[field.name, 'visibleLightRecorderId']} style={{ marginBottom: 0 }}>
                    <Select options={robotOptions} {...commonSelectProps} />
                  </Form.Item>
                ),
              },
              {
                title: '红外光',
                width: 300,
                dataIndex: 'infraredRecorderId',
                render: (_: unknown, field) => (
                  <Form.Item name={[field.name, 'infraredRecorderId']} style={{ marginBottom: 0 }}>
                    <Select options={robotOptions} {...commonSelectProps} />
                  </Form.Item>
                ),
              },
              {
                title: '操作',
                dataIndex: 'action',
                width: 120,
                render: (_: unknown, field) => (
                  <Button type="link" disabled={!isEdit} onClick={() => remove(field.name)}>
                    删除
                  </Button>
                ),
              },
            ]}
          />
          <Button
            onClick={() => add()}
            type="dashed"
            disabled={!isEdit}
            icon={<PlusOutlined />}
            wrapClassName={styles.inspectionConfigAdd}
            style={{ width: '100%' }}
          >
            新增一行数据
          </Button>
        </div>
      )}
    </Form.List>
  );

  // 能流表头
  const energyFlowHeaders = ['用能分析组选择', '展示层级', '介质选择', '展示用能分析组名称'];
  return (
    <Spin
      spinning={detailLoading || optionsLoading || saveLoading || mediumLoadingCount > 0}
      wrapperClassName={styles.pageSpin}
    >
      <Form form={form} layout="horizontal" onFinish={onFinish}>
        <div style={{ padding: 20 }}>
          <FormTitle title="能源概览配置" />
          <SubContent title="用能配置">
            <Form.List name="energyStatisticsUnits">
              {fields => (
                <>
                  {fields.map((field, idx) => (
                    <EnergyStatisticsRow
                      key={field.key}
                      index={idx}
                      isEdit={isEdit}
                      placeholder={'请选择'}
                      unitGroup={unitGroup}
                      form={form}
                      onLoadingChange={handleMediumLoadingChange}
                    />
                  ))}
                </>
              )}
            </Form.List>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="periodId" label="生产时段">
                  <Select options={nonWorkingPeriods} {...commonSelectProps} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nonPeriodId" label="非生产时段">
                  <Select {...commonSelectProps} options={nonWorkingPeriods} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="pvCircuitId"
                  label={<span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>光伏回路</span>}
                >
                  <Select {...commonSelectProps} options={circuitIdOp} mode={'multiple'} />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          <SubContent title="巡检配置">
            {renderInspectionConfigList('robotConfigs', '巡检机器人')}
            {renderInspectionConfigList('dogConfigs', '巡检机器狗')}
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="inspectionRecord" label="巡检记录仪">
                  <Select {...commonSelectProps} options={inspectionRecorderOptions} mode="multiple" />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          <SubContent title="能流配置">
            <div style={{ marginBottom: '6px' }}>
              <Row gutter={44}>
                {energyFlowHeaders.map(header => (
                  <Col key={header} flex="0 0 345px" className={styles.energyFlowHeader}>
                    {header}
                  </Col>
                ))}
              </Row>
            </div>
            <Row gutter={44}>
              {Array.from({ length: 5 }, (_, i) => i + 1).map(index => (
                <EnergyFlowRow
                  key={index}
                  index={index}
                  isEdit={isEdit}
                  unitGroup={unitGroup}
                  onLoadingChange={handleMediumLoadingChange}
                />
              ))}
            </Row>
          </SubContent>
          <SubContent title="区域列表">
            <AreaListTable
              isEdit={isEdit}
              unitGroup={unitGroup}
              form={form}
              onLoadingChange={handleMediumLoadingChange}
            />
          </SubContent>
          <SubContent title="部门列表">
            <DeptListTable
              isEdit={isEdit}
              unitGroup={unitGroup}
              form={form}
              onLoadingChange={handleMediumLoadingChange}
            />
          </SubContent>
          <SubContent title="环保配置">
            <EnvConfig isEdit={isEdit} form={form} />
          </SubContent>
          <SubContent title="节能降碳">
            <EnergySavingConfig isEdit={isEdit} form={form} />
          </SubContent>
          {/* <SubContent title="配置文件上传">
            
            <Upload
              listType="text"
              accept={['.xlsx']}
              uploadText="选择导入文件"
              fileSize={50}
              tip="支持扩展名：.xlsx，不超过50MB"
            > 
            <Button type="link" size="small" href="/api/v2/outputProductBom/template/download">
              配置文件
            </Button>
            </Upload>
          </SubContent> */}
          <SubContent title="用能计划配置表">
            <Button type="link" onClick={() => setOpenUploadModal(true)}>
              配置表
            </Button>
          </SubContent>
        </div>
        {openUploadModal && <UploadImport title="用能计划配置表" setOpen={setOpenUploadModal} />}
        <Space className="sticky-footer" size={8}>
          {!isEdit ? (
            <>
              <Button
                type="primary"
                onClick={() => {
                  setIsEdit(true);
                }}
              >
                编辑
              </Button>
            </>
          ) : (
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  form.submit();
                }}
              >
                保存
              </Button>
              <Button
                onClick={() => {
                  if (isEdit) {
                    Modal.confirm({
                      title: '是否放弃所有未保存信息？',
                      onOk: () => {
                        setIsEdit(false);
                        form.resetFields();
                        setRefresh(prev => !prev);
                      },
                    });
                  }
                }}
              >
                取消
              </Button>
            </Space>
          )}
        </Space>
      </Form>
    </Spin>
  );
};
export default EnergyOverview;
